import { overtimePay as calculateOvertimePay } from "./monthlySalary";
import { calculateAbsencePay } from "./absencePay";
import {
  safeParseDate, buildEmployeeEntriesFromShift, mergeApprovedOvertimeForEmployee,
  mergeApprovedAbsenceForEmployee, calculateShiftHours, calculateOvertimeForShift,
  calculateAbsenceForShift, calculateObForShift,
} from "./payrollCalculations";

const dateKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

// Attribute each shift to its scheduled date, as in payroll export.
// Spread fixed monthly pay over calendar days so daily amounts sum to monthly pay.
export function calculateDailyPersonnelCost(data, selectedDate) {
  return calculatePersonnelCost(data, selectedDate, false);
}

export function calculateMonthlyPersonnelCost(data, selectedMonth) {
  return calculatePersonnelCost(data, selectedMonth, true);
}

function calculatePersonnelCost(data, selectedDate, monthly) {
  const { employees = [], shifts = [], bookings = [], obRules = [], holidays = [],
    overtimeRules = [], overtimeRequests = [], absenceTypes = [], absenceRequests = [], customObEntries = [] } = data;
  const [year, month] = selectedDate.split("-").map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();
  const totals = new Map(employees.map((employee) => [employee.id, { hours: 0, overtime: 0, overtimePremiumHours: 0, absenceHours: 0, absenceValue: 0, ob: 0 }]));
  const employeeMap = new Map(employees.map((employee) => [employee.id, employee]));
  shifts.forEach((shift) => {
    const date = safeParseDate(shift.date);
    if (!date || (monthly ? dateKey(date).slice(0, 7) : dateKey(date)) !== selectedDate) return;
    const entries = new Map(buildEmployeeEntriesFromShift(shift).map((entry) => [entry.employeeId, entry.employeeData]));
    bookings.forEach((booking) => {
      if (["approved", "reserved"].includes(booking.status) && booking.userId &&
          String(booking.originalShiftId || booking.shiftId) === String(shift.originalId || shift.id) && !entries.has(booking.userId)) {
        entries.set(booking.userId, { breaks: [], overtime: [], absences: [] });
      }
    });
    entries.forEach((entry, employeeId) => {
      const employee = employeeMap.get(employeeId);
      if (!employee) return;
      const payroll = employee.payroll || {};
      const withOvertime = mergeApprovedOvertimeForEmployee({ shift, employeeId, employeeData: entry, employees, overtimeRules,
        approvedOvertimeRequests: overtimeRequests.filter((request) => request.status === "approved") });
      const employeeData = mergeApprovedAbsenceForEmployee({ shift, employeeId, employeeData: withOvertime, employees, absenceTypes,
        approvedAbsenceRequests: absenceRequests.filter((request) => request.status === "approved") });
      const overtime = calculateOvertimeForShift(shift, employeeData);
      const absence = calculateAbsenceForShift(shift, employeeData);
      const total = totals.get(employeeId);
      total.hours += calculateShiftHours(shift, employeeData) + overtime.hours;
      total.overtime += overtime.premiumValue;
      total.overtimePremiumHours += overtime.premiumHours;
      total.absenceHours += absence.hours;
      total.absenceValue += absence.value;
      total.ob += calculateObForShift(shift, obRules, holidays, payroll.obEnabled ?? true, employeeData,
        customObEntries.filter((entry) => entry.employeeId === employeeId)).value;
    });
  });
  let gross = 0;
  let missingSalaryCount = 0;
  employees.forEach((employee) => {
    const payroll = employee.payroll || {};
    const total = totals.get(employee.id);
    const rate = Number(payroll.savedPayAmount || 0);
    if (!payroll.savedSalaryTypeId || !Number.isFinite(rate) || rate <= 0) {
      if (total.hours > 0 || total.absenceHours > 0) missingSalaryCount++;
      return;
    }
    const payType = payroll.savedPayType || "hourly";
    const absencePay = calculateAbsencePay({ hours: total.absenceHours, value: total.absenceValue }, payType, rate, employee);
    const basePay = payType === "monthly" ? (monthly ? rate : rate / daysInMonth) : rate * (total.hours + absencePay.paidHours);
    const overtimePay = calculateOvertimePay({ premiumValue: total.overtime, premiumHours: total.overtimePremiumHours }, payType, rate, employee);
    const vacationPay = (payroll.semEnabled ?? true) ? basePay * 0.12 * (Number(payroll.semProcent || 100) / 100) : 0;
    gross += basePay + overtimePay + total.ob + vacationPay - absencePay.deduction;
  });
  return { gross, missingSalaryCount };
}
